import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { useSearchParams } from 'react-router-dom';
import CourseCard from '../components/CourseCard';
import Spinner from '../components/Spinner';
import { motion, AnimatePresence } from 'framer-motion';
import { FunnelIcon, XMarkIcon, MagnifyingGlassIcon, CheckCircleIcon, BookOpenIcon, AcademicCapIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';
import { AuthContext } from '../context/AuthContext';

const CoursesPage = () => {
    const { token } = useContext(AuthContext);
    const [courses, setCourses] = useState([]);
    const [categories, setCategories] = useState([]);
    const [tags, setTags] = useState([]);
    const [enrollments, setEnrollments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchTerm, setSearchTerm] = useState('');
    const [showFilters, setShowFilters] = useState(true);
    const [enrollmentFilter, setEnrollmentFilter] = useState('all'); // all, enrolled, notEnrolled, completed

    const selectedCategory = searchParams.get('category');
    const selectedTag = searchParams.get('tag');

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const requests = [
                    axios.get(`${API_BASE_URL}/api/courses/categories`),
                    axios.get(`${API_BASE_URL}/api/courses/tags`),
                    axios.get(`${API_BASE_URL}/api/courses`, {
                        params: {
                            category: selectedCategory,
                            tag: selectedTag
                        }
                    })
                ];

                // Eğer kullanıcı giriş yapmışsa enrollment bilgilerini de al
                if (token) {
                    requests.push(
                        axios.get(`${API_BASE_URL}/api/users/me/profile`, {
                            headers: { 'x-auth-token': token }
                        })
                    );
                }

                const results = await Promise.all(requests);
                setCategories(results[0].data);
                setTags(results[1].data);
                setCourses(results[2].data);
                
                if (token && results[3]) {
                    setEnrollments(results[3].data.enrollments || []);
                }
            } catch (err) {
                console.error("Kurslar/Etiketler/Kategoriler getirilirken hata:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [selectedCategory, selectedTag, token]);

    const updateSearchParams = (key, value) => {
        const newSearchParams = new URLSearchParams(searchParams);
        if (value) {
            newSearchParams.set(key, value);
        } else {
            newSearchParams.delete(key);
        }
        setSearchParams(newSearchParams);
    };

    const handleCategoryClick = (category) => {
        updateSearchParams('tag', null);
        updateSearchParams('category', category === selectedCategory ? null : category);
    };

    const handleTagClick = (tag) => {
        updateSearchParams('tag', tag === selectedTag ? null : tag);
    };

    const clearAllFilters = () => {
        setSearchParams({});
        setSearchTerm('');
        setEnrollmentFilter('all');
    };

    // Enrollment helper functions
    const isEnrolled = (courseId) => {
        return enrollments.some(e => e.course && e.course._id === courseId);
    };

    const isCompleted = (courseId) => {
        return enrollments.some(e => e.course && e.course._id === courseId && (e.completed || e.certificateEarned));
    };

    // Search and enrollment filter
    const filteredCourses = courses.filter(course => {
        // Search filter
        const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            course.description.toLowerCase().includes(searchTerm.toLowerCase());
        
        if (!matchesSearch) return false;

        // Enrollment filter
        if (enrollmentFilter === 'enrolled') {
            return isEnrolled(course._id) && !isCompleted(course._id);
        } else if (enrollmentFilter === 'notEnrolled') {
            return !isEnrolled(course._id);
        } else if (enrollmentFilter === 'completed') {
            return isCompleted(course._id);
        }
        
        return true; // 'all' - show everything
    });

    const hasActiveFilters = selectedCategory || selectedTag || searchTerm || enrollmentFilter !== 'all';

    return (
        <>
            <SEO 
                title="Aviation & Aerospace Courses - Online Learning | AircoreDynamics"
                description="Specialize in aviation and aerospace technologies with comprehensive online courses. Learn from beginner to advanced level at your own pace."
                keywords="aviation courses, aerospace courses, online aviation training, aerodynamics course, aircraft design course, flight dynamics"
                image="https://aircoredynamics.com/og-image.png"
                url="/courses"
                type="website"
            />
            <div className="container mx-auto px-6 py-8 space-y-6">
                {/* Header Section */}
                <motion.div 
                    initial={{ opacity: 0, y: -20 }} 
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center space-y-3 py-4"
                >
                    <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-light-primary to-blue-500 dark:from-dark-primary dark:to-cyan-400 bg-clip-text text-transparent">
                        Courses
                    </h1>
                    <p className="text-lg opacity-70 max-w-2xl mx-auto">
                        Specialize in aviation and aerospace technologies. Choose the course that suits your needs and start learning.
                    </p>
                </motion.div>

                {/* Compact Filter Bar */}
                <motion.div 
                    initial={{ opacity: 0, y: 20 }} 
                    animate={{ opacity: 1, y: 0 }}
                    className="card p-3 max-w-2xl mx-auto"
                >
                    <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                            <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50" />
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full px-3 py-1.5 pl-8 text-sm rounded-lg border border-light-border dark:border-dark-border bg-light-bg dark:bg-dark-bg focus:outline-none focus:ring-2 focus:ring-light-primary dark:focus:ring-dark-primary"
                            />
                        </div>
                        
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className="btn-secondary flex items-center gap-1.5 text-xs px-3 py-1.5 whitespace-nowrap"
                        >
                            <FunnelIcon className="h-3.5 w-3.5" />
                            {hasActiveFilters && <span className="bg-light-primary dark:bg-dark-primary text-white text-[10px] px-1.5 py-0.5 rounded-full">
                                {[selectedCategory, selectedTag, enrollmentFilter !== 'all' ? 'status' : null].filter(Boolean).length}
                            </span>}
                        </button>
                        
                        {hasActiveFilters && (
                            <button
                                onClick={clearAllFilters}
                                className="text-red-500 hover:text-red-600 p-1.5"
                                title="Clear filters"
                            >
                                <XMarkIcon className="h-4 w-4" />
                            </button>
                        )}
                    </div>

                    <AnimatePresence>
                        {showFilters && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.15 }}
                                className="overflow-hidden space-y-2 pt-2 mt-2 border-t border-light-border dark:border-dark-border"
                            >
                                {/* Enrollment Status Filter - Only show if user is logged in */}
                                {token && (
                                    <div className="flex flex-wrap gap-1.5 items-center">
                                        <span className="text-[10px] font-semibold opacity-50 uppercase">Status:</span>
                                        <button
                                            onClick={() => setEnrollmentFilter('all')}
                                            className={`px-2 py-0.5 rounded text-[11px] font-medium transition-all flex items-center gap-1 ${
                                                enrollmentFilter === 'all'
                                                    ? 'bg-light-primary text-white dark:bg-dark-primary dark:text-dark-bg'
                                                    : 'bg-light-bg dark:bg-dark-bg hover:bg-light-border dark:hover:bg-dark-border'
                                            }`}
                                        >
                                            <AcademicCapIcon className="h-3 w-3" />
                                            All Courses
                                        </button>
                                        <button
                                            onClick={() => setEnrollmentFilter('enrolled')}
                                            className={`px-2 py-0.5 rounded text-[11px] font-medium transition-all flex items-center gap-1 ${
                                                enrollmentFilter === 'enrolled'
                                                    ? 'bg-blue-500 text-white'
                                                    : 'bg-light-bg dark:bg-dark-bg hover:bg-light-border dark:hover:bg-dark-border'
                                            }`}
                                        >
                                            <BookOpenIcon className="h-3 w-3" />
                                            My Enrolled
                                        </button>
                                        <button
                                            onClick={() => setEnrollmentFilter('completed')}
                                            className={`px-2 py-0.5 rounded text-[11px] font-medium transition-all flex items-center gap-1 ${
                                                enrollmentFilter === 'completed'
                                                    ? 'bg-green-500 text-white'
                                                    : 'bg-light-bg dark:bg-dark-bg hover:bg-light-border dark:hover:bg-dark-border'
                                            }`}
                                        >
                                            <CheckCircleIcon className="h-3 w-3" />
                                            Completed
                                        </button>
                                        <button
                                            onClick={() => setEnrollmentFilter('notEnrolled')}
                                            className={`px-2 py-0.5 rounded text-[11px] font-medium transition-all ${
                                                enrollmentFilter === 'notEnrolled'
                                                    ? 'bg-gray-500 text-white'
                                                    : 'bg-light-bg dark:bg-dark-bg hover:bg-light-border dark:hover:bg-dark-border'
                                            }`}
                                        >
                                            Not Enrolled
                                        </button>
                                    </div>
                                )}

                                {categories.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 items-center">
                                        <span className="text-[10px] font-semibold opacity-50 uppercase">Cat:</span>
                                        <button
                                            onClick={() => handleCategoryClick(null)}
                                            className={`px-2 py-0.5 rounded text-[11px] font-medium transition-all ${
                                                !selectedCategory
                                                    ? 'bg-light-primary text-white dark:bg-dark-primary dark:text-dark-bg'
                                                    : 'bg-light-bg dark:bg-dark-bg hover:bg-light-border dark:hover:bg-dark-border'
                                            }`}
                                        >
                                            All
                                        </button>
                                        {categories.map(category => (
                                            <button
                                                key={category}
                                                onClick={() => handleCategoryClick(category)}
                                                className={`px-2 py-0.5 rounded text-[11px] font-medium transition-all ${
                                                    selectedCategory === category
                                                        ? 'bg-light-primary text-white dark:bg-dark-primary dark:text-dark-bg'
                                                        : 'bg-light-bg dark:bg-dark-bg hover:bg-light-border dark:hover:bg-dark-border'
                                                }`}
                                            >
                                                {category}
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {tags.length > 0 && (
                                    <div className="flex flex-wrap gap-1 items-center">
                                        <span className="text-[10px] font-semibold opacity-50 uppercase">Tags:</span>
                                        <button
                                            onClick={() => handleTagClick(null)}
                                            className={`px-2 py-0.5 rounded-full text-[11px] border transition-all ${
                                                !selectedTag
                                                    ? 'bg-light-primary text-white dark:bg-dark-primary dark:text-dark-bg border-transparent'
                                                    : 'border-light-border dark:border-dark-border hover:border-light-primary dark:hover:border-dark-primary'
                                            }`}
                                        >
                                            All
                                        </button>
                                        {tags.map(tag => (
                                            <button
                                                key={tag}
                                                onClick={() => handleTagClick(tag)}
                                                className={`px-2 py-0.5 rounded-full text-[11px] border transition-all ${
                                                    selectedTag === tag
                                                        ? 'bg-light-primary text-white dark:bg-dark-primary dark:text-dark-bg border-transparent'
                                                        : 'border-light-border dark:border-dark-border hover:border-light-primary dark:hover:border-dark-primary'
                                                }`}
                                            >
                                                #{tag}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>

                {!loading && (
                    <div className="text-xs opacity-60 text-center">
                        {filteredCourses.length} result{filteredCourses.length !== 1 ? 's' : ''}
                    </div>
                )}

                {/* Courses Grid */}
            {loading ? (
                <Spinner />
            ) : filteredCourses.length > 0 ? (
                <motion.div
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                    initial="hidden"
                    animate="visible"
                    variants={{
                        visible: {
                            transition: {
                                staggerChildren: 0.1
                            }
                        }
                    }}
                >
                    {filteredCourses.map((course, idx) => (
                        <motion.div
                            key={course._id}
                            variants={{
                                hidden: { opacity: 0, y: 20 },
                                visible: { opacity: 1, y: 0 }
                            }}
                            transition={{ duration: 0.4 }}
                        >
                            <CourseCard course={course} />
                        </motion.div>
                    ))}
                </motion.div>
            ) : (
                <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="card p-12 text-center"
                >
                    <div className="max-w-md mx-auto">
                        <div className="text-6xl mb-4">🔍</div>
                        <h3 className="text-xl font-semibold mb-2">No Courses Found</h3>
                        <p className="text-sm opacity-70 mb-6">
                            {hasActiveFilters 
                                ? 'No courses found matching your filters. Try changing the filters.'
                                : 'No courses have been added yet.'}
                        </p>
                        {hasActiveFilters && (
                            <button onClick={clearAllFilters} className="btn-secondary">
                                Clear Filters
                            </button>
                        )}
                    </div>
                </motion.div>
            )}
        </div>
        </>
    );
};

export default CoursesPage;